import { useState, useEffect, useCallback, useRef } from 'react';

const DEFAULT_INTERVAL_OPTIONS = [30, 45, 60, 90, 120, 180, 240, 300];

/**
 * Auto-refresh hook: provides on/off toggle, interval selector, and last-updated timestamp.
 * @param {Function} fetchFn - the function to call on each tick (receives AbortSignal)
 * @param {Object} opts
 * @param {number} opts.defaultInterval - default interval in seconds (default 60)
 * @param {boolean} opts.defaultEnabled - default toggle state (default false)
 * @param {number[]} opts.intervalOptions - dropdown options (default [30..300])
 */
export function useAutoRefresh(
  fetchFn,
  { defaultInterval = 60, defaultEnabled = false, intervalOptions = DEFAULT_INTERVAL_OPTIONS } = {},
) {
  const [enabled, setEnabled] = useState(defaultEnabled);
  const [interval, setInterval_] = useState(defaultInterval);
  const [lastUpdated, setLastUpdated] = useState(null);
  const fetchRef = useRef(fetchFn);

  useEffect(() => {
    fetchRef.current = fetchFn;
  }, [fetchFn]);

  const doRefresh = useCallback(() => {
    const controller = new AbortController();
    fetchRef.current(controller.signal);
    setLastUpdated(new Date());
    return controller;
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(() => {
      doRefresh();
    }, interval * 1000);
    return () => window.clearInterval(id);
  }, [enabled, interval, doRefresh]);

  return {
    enabled,
    setEnabled,
    interval,
    setInterval: setInterval_,
    lastUpdated,
    setLastUpdated,
    intervalOptions,
  };
}
